import {
  AdminInput,
  AdminSwitch,
  Box,
  CollapsibleComponent,
  Div,
  H5,
  P,
  SubTitleWithTooltipIcon,
  useElementHeight,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useRef } from 'react';

export const DELIVERY_NOW_REASSIGN_MIN_MINUTES = 1;
export const DELIVERY_NOW_REASSIGN_MAX_MINUTES = 100;
/** Suggested when the toggle is switched on, so the field is never sent empty. */
export const DELIVERY_NOW_REASSIGN_DEFAULT_MINUTES = 4;

type Props = {
  enabled: boolean;
  setEnabled: Dispatch<SetStateAction<boolean>>;
  minutes: number | null;
  setMinutes: Dispatch<SetStateAction<number | null>>;
};

export const DeliveryNowReassign = ({
  enabled,
  setEnabled,
  minutes,
  setMinutes,
}: Props) => {
  const minutesRef = useRef<HTMLInputElement>(null);
  const { ref, height } = useElementHeight<HTMLDivElement>();

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    if (!next) return;
    // Turning it off keeps the number - the backend stores it either way - so
    // switching back on does not make the user type it again.
    if (!minutes) setMinutes(DELIVERY_NOW_REASSIGN_DEFAULT_MINUTES);
    setTimeout(() => minutesRef.current?.focus(), 0);
  };

  const handleMinutesChange = (value: string) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (!digits) {
      setMinutes(null);
      return;
    }
    // Backend rejects anything outside 1-100, so the field never holds one.
    setMinutes(
      Math.min(Number(digits), DELIVERY_NOW_REASSIGN_MAX_MINUTES) ||
        DELIVERY_NOW_REASSIGN_MIN_MINUTES
    );
  };

  return (
    <>
      <H5 $m="0">DeliveryNow</H5>
      <P $m="0">
        If <b>DeliveryNow</b> does not find a driver in the given time, the
        delivery is cancelled and the order is automatically re-dispatched to
        the next fleet in this strategy, with DeliveryNow left out of that
        round. Cancelling carries the standard cancellation fee, and the new
        delivery is charged at the new fleet&apos;s price.
      </P>
      <Box $flex $justifyCenter $between $column>
        {/*
         * The row carries the spacing that Box's 70px min-height gives it while
         * collapsed (12px of Box padding + 8px of centring around a 28px switch,
         * inside a 70px border-box).
         * Left to the min-height, that centring stops the moment the minutes
         * field expands the box past 70px and the whole thing visibly tightens
         * on being switched on; owned by the row, it holds in both states and
         * the collapsed box still measures the same 70px (60px on mobile).
         */}
        <Div $flex $alignCenter $between $gap="16px" $p="8px 0" $pSM="3px 0">
          <SubTitleWithTooltipIcon
            mb="0"
            text={'Auto-Reassign DeliveryNow'}
            tooltip={`
            Applies to the <b>DeliveryNow</b> fleet only. The replacement fleet is picked by the same rules
            as any other assignment - distance, timeframe and banned providers.
            <ul style="margin: 8px 0; padding-left: 20px;">
              <li>The timer starts when the delivery starts looking for a driver, not when a scheduled order is placed.</li>
              <li>One attempt per order - if the replacement fleet finds no driver either, no new round is started.</li>
              <li>If a driver is assigned before the time is up, nothing happens.</li>
            </ul>
          `}
          />

          <AdminSwitch checked={enabled} onChange={handleToggle} />
        </Div>
        <CollapsibleComponent expanded={enabled} elementHeight={height ?? 0}>
          <Div
            ref={ref}
            $flex
            $alignCenter
            $between
            $gap="12px"
            $p="20px 0 8px"
            $pSM="20px 0 3px"
          >
            <P $m="0">Cancel and reassign if no driver is assigned after:</P>
            <Div $width="100px" $flexNone>
              <AdminInput
                placeholder="0"
                value={minutes ?? ''}
                onChange={(e) => handleMinutesChange(e.target.value)}
                ref={minutesRef}
                $textCenter
                suffix="min"
              />
            </Div>
          </Div>
        </CollapsibleComponent>
      </Box>
    </>
  );
};
